import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useCart } from '@/context/cart'
import { useMiniCart } from '@/context/mini-cart'
import { AlertCircle, Package } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

const MiniCart = () => {
    const navigate = useNavigate()
    const { isOpen, setIsOpen } = useMiniCart()
    const { state, dispatch } = useCart()
    const [duplicateErrors, setDuplicateErrors] = useState<{ [key: string]: string }>({})

    useEffect(() => {
        const checkForDuplicates = () => {
            const newErrors: { [key: string]: string } = {}

            try {
                const agentBundleMap = new Map<string, string>()

                state.items.forEach(item => {
                    if (item && item.wizards && Array.isArray(item.wizards)) {
                        item.wizards.forEach(wizard => {
                            if (wizard && wizard.id) {
                                agentBundleMap.set(wizard.id, item.name)
                            }
                        })
                    }
                })

                state.items.forEach(item => {
                    if (!item || !item.wizardId || (item.wizards && item.wizards.length)) return

                    const bundleName = agentBundleMap.get(item.wizardId)
                    if (bundleName) {
                        newErrors[item.id] =
                            `This agent is already included in the "${bundleName}" bundle in your cart.`
                    }
                })
            } catch (error) {
                console.error('Error detecting duplicates:', error)
            }

            setDuplicateErrors(newErrors)
        }

        checkForDuplicates()

        const timeoutId = setTimeout(() => {
            checkForDuplicates()
        }, 100)

        return () => clearTimeout(timeoutId)
    }, [state.items])

    useEffect(() => {
        const currentItemIds = new Set(state.items.map(item => item.id))

        // Remove any errors for items that are no longer in the cart
        setDuplicateErrors(prevErrors => {
            const newErrors = { ...prevErrors }
            Object.keys(newErrors).forEach(itemId => {
                if (!currentItemIds.has(itemId)) {
                    delete newErrors[itemId]
                }
            })
            return newErrors
        })
    }, [state.items])

    const updateQuantity = (id: string, quantity: number) => {
        if (quantity < 1) {
            dispatch({ type: 'REMOVE_ITEM', payload: id })
        } else {
            dispatch({ type: 'UPDATE_QUANTITY', payload: { id, quantity } })
        }
    }

    const removeItem = (id: string) => {
        dispatch({ type: 'REMOVE_ITEM', payload: id })
    }

    const handleNavigation = async (url: string) => {
        localStorage.setItem('cart', JSON.stringify({ items: state.items, total: state.total }))
        setIsOpen(false)

        await new Promise(resolve => setTimeout(resolve, 100))
        navigate(url)
    }

    return (
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetContent>
                <SheetHeader>
                    <SheetTitle className='flex items-center gap-2 text-lg font-semibold'>
                        Shopping cart
                    </SheetTitle>
                </SheetHeader>

                <div className='relative mt-2 w-[450px]'>
                    <div className='h-full p-6 overflow-y-auto'>
                        {/* Cart Items */}
                        <div className='flex-1 overflow-y-auto'>
                            {state.items.length === 0 ? (
                                <div className='py-8 text-center'>
                                    <p className='text-gray-500'>Your cart is empty</p>
                                    <Link
                                        to='/dashboard/flows/store'
                                        onClick={() => setIsOpen(false)}
                                        className='inline-block mt-4 font-medium text-purple-600 hover:text-purple-700'
                                    >
                                        Continue Shopping
                                    </Link>
                                </div>
                            ) : (
                                <div className='mb-3 space-y-4'>
                                    {state.items.map(item => (
                                        <div key={item.id} className='flex flex-col'>
                                            <div className='p-4 border rounded-lg'>
                                                <div className='flex items-start gap-4'>
                                                    <div className='flex items-center justify-center flex-shrink-0 w-12 h-12 text-xl bg-gray-100 rounded-lg'>
                                                        {item.wizards && item.wizards.length > 0 ? (
                                                            <Package className='w-8 h-8 text-purple-500' />
                                                        ) : (
                                                            '🧙‍♂️'
                                                        )}
                                                    </div>
                                                    <div className='flex-1 min-w-0'>
                                                        <Link
                                                            to={`/dashboard/flows/store/${
                                                                item.category
                                                                    ? item.category.toLowerCase()
                                                                    : item.wizards && item.wizards.length > 0
                                                                      ? 'bundle'
                                                                      : 'all'
                                                            }/${item.slug}`}
                                                            className='text-sm font-medium text-gray-900 hover:text-purple-600'
                                                            onClick={e => {
                                                                e.preventDefault()
                                                                handleNavigation(
                                                                    `/dashboard/flows/store/${
                                                                        item.category
                                                                            ? item.category.toLowerCase()
                                                                            : item.wizards && item.wizards.length > 0
                                                                              ? 'bundle'
                                                                              : 'all'
                                                                    }/${item.slug}`
                                                                )
                                                            }}
                                                        >
                                                            {item.name}
                                                        </Link>
                                                        <div className='flex items-center gap-4 mt-1'>
                                                            <div className='flex items-center border rounded'>
                                                                <button
                                                                    onClick={() =>
                                                                        updateQuantity(item.id, item.quantity - 1)
                                                                    }
                                                                    className='px-2 py-1 text-gray-600 hover:text-gray-800'
                                                                >
                                                                    -
                                                                </button>
                                                                <span className='px-2 py-1 text-sm text-gray-900'>
                                                                    {item.quantity}
                                                                </span>
                                                                <button
                                                                    onClick={() =>
                                                                        updateQuantity(item.id, item.quantity + 1)
                                                                    }
                                                                    className='px-2 py-1 text-gray-600 hover:text-gray-800'
                                                                >
                                                                    +
                                                                </button>
                                                            </div>
                                                            <button
                                                                onClick={() => removeItem(item.id)}
                                                                className='text-sm text-red-600 hover:text-red-700'
                                                            >
                                                                Remove
                                                            </button>
                                                        </div>
                                                        <div className='mt-1 text-sm font-medium text-gray-900'>
                                                            Price: ${item.price}
                                                        </div>
                                                    </div>
                                                </div>

                                                {duplicateErrors[item.id] && (
                                                    <div className='flex items-start gap-2 p-3 mt-2 text-red-600 rounded-md bg-red-50'>
                                                        <AlertCircle className='h-5 w-5 flex-shrink-0 mt-0.5' />
                                                        <div className='text-sm'>
                                                            {duplicateErrors[item.id]}
                                                            <button
                                                                onClick={() => removeItem(item.id)}
                                                                className='ml-2 font-medium underline'
                                                            >
                                                                Remove
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        {state.items.length > 0 && (
                            <div className='p-4 border-t border-gray-200 bg-gray-50'>
                                <div className='mb-4'>
                                    <div className='flex justify-between mb-2 text-sm text-gray-600'>
                                        <span>Subtotal</span>
                                        <span>${state.total.toFixed(2)}</span>
                                    </div>
                                    <div className='flex justify-between font-medium'>
                                        <span>Total</span>
                                        <span>${state.total.toFixed(2)}</span>
                                    </div>
                                </div>
                                <div className='space-y-2'>
                                    <Link
                                        to='/ai-agents/store/cart'
                                        className='block w-full px-4 py-2 font-medium text-center text-white transition-colors bg-purple-600 rounded-lg hover:bg-purple-700'
                                        onClick={e => {
                                            e.preventDefault()
                                            handleNavigation('/dashboard/flows/store/cart')
                                        }}
                                    >
                                        View Cart
                                    </Link>
                                    <Link
                                        to='/ai-agents/store/checkout'
                                        className='block w-full px-4 py-2 font-medium text-center text-white transition-colors bg-blue-600 rounded-lg hover:bg-blue-700'
                                        onClick={e => {
                                            e.preventDefault()
                                            handleNavigation('/dashboard/flows/store/checkout')
                                        }}
                                    >
                                        Checkout
                                    </Link>
                                    <Link
                                        to='/dashboard/flows/store'
                                        className='block w-full px-4 py-2 text-sm text-center text-gray-600 hover:text-gray-800'
                                        onClick={e => {
                                            e.preventDefault()
                                            handleNavigation('/dashboard/flows/store')
                                        }}
                                    >
                                        Continue Shopping
                                    </Link>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    )
}

export default MiniCart
